<template>
  <div v-show="showStreamManager">
    <q-card square flat bordered class="my-card bg-grey-1 vertical-bottom">
      <q-toolbar>
        <q-toolbar-title class="text-weight-bold text-grey-brand">
          Stream Manager
        </q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onClose" />
      </q-toolbar>

      <q-separator />

      <q-toolbar dense class="q-mt-xs">
        <q-toolbar-title
          class="text-weight-bold text-grey-brand text-subtitle2"
        >
          Live Streams
        </q-toolbar-title>
        <q-btn flat round dense icon="refresh" size="sm" @click="refresh">
          <q-tooltip>Refresh sessions</q-tooltip>
        </q-btn>
      </q-toolbar>

      <q-card-section class="q-pt-none q-pb-sm">
        <div class="row items-center q-gutter-sm">
          <q-select
            v-model="selectedDevice"
            :options="filteredDeviceOptions"
            label="Select device"
            dense
            outlined
            option-label="label"
            option-value="uid"
            emit-value
            map-options
            use-input
            input-debounce="200"
            class="col"
            :loading="loadingDevices"
            @popup-show="fetchDevices"
            @filter="onFilterDevices"
          >
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge
                    :color="scope.opt.isConnected ? 'green' : 'grey'"
                    :label="scope.opt.isConnected ? 'online' : 'offline'"
                  />
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-btn
            dense
            :outline="!onlineOnly"
            :color="onlineOnly ? 'green' : 'grey'"
            icon="wifi"
            @click="onlineOnly = !onlineOnly"
          >
            <q-tooltip>{{
              onlineOnly ? 'Showing online only' : 'Showing all devices'
            }}</q-tooltip>
          </q-btn>
          <q-btn
            dense
            color="primary"
            label="Start"
            :disable="selectedDevice === null"
            @click="startSession"
          />
        </div>
      </q-card-section>

      <q-card-section
        v-if="activeDeviceSessions.length === 0"
        class="q-pt-none"
      >
        <div class="text-grey-6 text-center q-py-md">
          No active stream sessions
        </div>
      </q-card-section>

      <q-list v-else separator>
        <q-item
          v-for="device in activeDeviceSessions"
          :key="device.uid"
          class="q-py-sm"
          :class="{ 'stream-active-item': isStreamOpen(device.uid) }"
        >
          <q-item-section>
            <q-item-label class="text-weight-medium">
              UID {{ device.uid }}
            </q-item-label>
            <q-item-label caption>
              {{ device.activeSessionCount }}
              {{ device.activeSessionCount === 1 ? 'viewer' : 'viewers' }}
            </q-item-label>
            <q-item-label
              v-for="session in device.sessions"
              :key="session.streamSessionId"
              caption
              class="q-ml-sm"
            >
              {{ session.userName }}
              <span class="text-grey-5">
                — joined {{ formatTime(session.createdDate) }}
              </span>
            </q-item-label>
          </q-item-section>
          <q-item-section side style="min-width: 80px">
            <div class="row no-wrap items-center justify-end">
              <q-btn
                v-if="!isStreamOpen(device.uid)"
                flat
                dense
                color="primary"
                icon="play_arrow"
                @click="openStream(device.uid)"
              >
                <q-tooltip>Open stream</q-tooltip>
              </q-btn>
              <q-icon
                v-else
                name="sensors"
                color="green"
                size="sm"
                class="q-mr-xs"
              >
                <q-tooltip>Active in realtime plot</q-tooltip>
              </q-icon>
              <q-btn
                v-if="isViewerOn(device)"
                flat
                dense
                color="negative"
                icon="logout"
                @click="leaveStream(device.uid)"
              >
                <q-tooltip>Leave session</q-tooltip>
              </q-btn>
              <div v-else style="width: 33px" />
            </div>
          </q-item-section>
        </q-item>
      </q-list>

      <q-separator class="q-mt-sm" />

      <!-- Stream Recording History -->
      <q-toolbar dense class="q-mt-xs">
        <q-toolbar-title
          class="text-weight-bold text-grey-brand text-subtitle2"
        >
          Stream Recordings
        </q-toolbar-title>
        <q-btn
          flat
          round
          dense
          size="sm"
          :color="activeFilterCount > 0 ? 'primary' : ''"
          icon="filter_list"
          @click="toggleFilters"
        >
          <q-badge
            v-if="activeFilterCount > 0"
            color="primary"
            floating
            rounded
          >
            {{ activeFilterCount }}
          </q-badge>
          <q-tooltip>{{
            showFilters ? 'Hide filters' : 'Show filters'
          }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          dense
          icon="refresh"
          size="sm"
          @click="fetchRecordings"
        >
          <q-tooltip>Refresh recordings</q-tooltip>
        </q-btn>
      </q-toolbar>

      <q-slide-transition>
        <q-card-section v-show="showFilters" class="q-pt-none q-pb-sm">
          <div class="column q-gutter-sm">
            <q-input
              v-model.number="recordingUidFilter"
              dense
              outlined
              clearable
              type="number"
              label="Filter by UID"
              @keyup.enter="applyFilters"
              @clear="applyFilters"
            />
            <q-select
              v-model="selectedPigRunId"
              :options="pigRunOptions"
              label="Filter by pig run"
              dense
              outlined
              clearable
              emit-value
              map-options
              option-label="label"
              option-value="value"
              :loading="loadingPigRuns"
            />
            <div class="row justify-end q-gutter-sm">
              <q-btn
                flat
                dense
                label="Clear"
                :disable="activeFilterCount === 0"
                @click="clearFilters"
              />
              <q-btn
                dense
                color="primary"
                label="Apply"
                @click="applyFilters"
              />
            </div>
          </div>
        </q-card-section>
      </q-slide-transition>

      <q-card-section v-if="filteredRecordings.length === 0" class="q-pt-none">
        <div class="text-grey-6 text-center q-py-sm text-caption">
          No recordings found
        </div>
      </q-card-section>

      <q-list v-else dense>
        <div
          v-for="rec in filteredRecordings"
          :key="rec.streamRecordingId"
          class="recording-row"
          :class="{
            'playback-active-item':
              rec.streamRecordingId === activePlaybackRecordingId,
          }"
        >
          <q-item class="q-py-xs">
            <q-item-section>
              <q-item-label class="text-weight-medium text-caption">
                UID {{ rec.uid }}
              </q-item-label>
              <q-item-label v-if="rec.pigRunId" caption class="text-primary">
                <q-icon name="route" size="xs" class="q-mx-xs" />
                Pigrun:
                {{ rec.pigRunName }}
              </q-item-label>
              <q-item-label
                v-if="rec.pigRunMarkerName"
                caption
                class="text-primary"
              >
                <q-icon name="place" size="xs" class="q-mx-xs" />
                Marker:
                {{ rec.pigRunMarkerName }}
              </q-item-label>
              <q-item-label caption>
                {{ formatRecordingTime(rec.startedDateUtc) }}
                <span v-if="rec.stoppedDateUtc">
                  —
                  {{
                    formatRecordingDuration(
                      rec.startedDateUtc,
                      rec.stoppedDateUtc
                    )
                  }}
                </span>
                <q-badge
                  v-if="rec.isActive"
                  color="red"
                  label="recording"
                  class="q-ml-xs"
                />
              </q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-btn
                flat
                dense
                size="sm"
                :color="rec.notes ? 'primary' : 'grey-6'"
                :icon="rec.notes ? 'sticky_note_2' : 'note_add'"
                @click="toggleNotes(rec.streamRecordingId)"
              >
                <q-tooltip>{{
                  rec.notes ? 'View / edit note' : 'Add note'
                }}</q-tooltip>
              </q-btn>
            </q-item-section>
            <q-item-section side>
              <q-icon
                v-if="rec.streamRecordingId === activePlaybackRecordingId"
                name="sensors"
                color="green"
                size="sm"
              >
                <q-tooltip>Currently playing</q-tooltip>
              </q-icon>
              <q-btn
                v-else-if="rec.isActive"
                flat
                dense
                color="red"
                icon="stop"
                size="sm"
                @click="stopRecording(rec.streamRecordingId)"
              >
                <q-tooltip>Stop recording</q-tooltip>
              </q-btn>
              <q-btn
                v-else
                flat
                dense
                color="primary"
                icon="play_arrow"
                size="sm"
                @click="playbackRecording(rec.streamRecordingId)"
              >
                <q-tooltip>Play back recording</q-tooltip>
              </q-btn>
            </q-item-section>
            <q-item-section
              v-if="
                !rec.isActive &&
                rec.streamRecordingId !== activePlaybackRecordingId
              "
              side
            >
              <q-btn
                flat
                dense
                color="negative"
                icon="delete"
                size="sm"
                @click="confirmDeleteRecording(rec.streamRecordingId)"
              >
                <q-tooltip>Delete recording</q-tooltip>
              </q-btn>
            </q-item-section>
          </q-item>

          <q-slide-transition>
            <div
              v-show="expandedRecordingId === rec.streamRecordingId"
              class="q-px-md q-pb-sm bg-grey-2"
            >
              <q-input
                v-model="notesDraft"
                type="textarea"
                outlined
                dense
                autogrow
                label="Notes"
                :disable="savingNotes"
              />
              <div class="row justify-end q-gutter-sm q-mt-xs">
                <q-btn
                  flat
                  dense
                  label="Cancel"
                  :disable="savingNotes"
                  @click="cancelNotesEdit"
                />
                <q-btn
                  dense
                  color="primary"
                  label="Save"
                  :loading="savingNotes"
                  @click="saveNotes(rec)"
                />
              </div>
            </div>
          </q-slide-transition>
        </div>
      </q-list>
    </q-card>
    <InstructionCard
      v-model="showInstructions"
      title="Stream Manager"
      icon="sensors"
      message="Start or join a live stream, or record a stream and play it back later. You will find recordings in the Stream Recordings section."
      tip="Use the filter icon to narrow recordings by UID or Pig Run."
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Device } from '../models/Device';
import StreamService from '../services/StreamService';
import PigRunService from '../services/PigRunService';
import InstructionCard from './InstructionCard.vue';
import {
  isBackgroundRecordingRefresh,
  recordingsAfterFailedFetch,
  subscribeRecordingRefresh,
} from '../utils/StreamRecordingRefresh';

export default defineComponent({
  name: 'StreamManager',
  components: { InstructionCard },
  data() {
    return {
      selectedDevice: null as number | null,
      loadingDevices: false,
      onlineOnly: false,
      uidFilter: '',
      recordingUidFilter: null as number | null,
      recordings: [] as any[],
      pigRuns: [] as { pigRunId: number; name: string }[],
      selectedPigRunId: null as number | null,
      loadingPigRuns: false,
      showFilters: false,
      showInstructions: false,
      expandedRecordingId: null as number | null,
      notesDraft: '',
      savingNotes: false,
      unsubscribeAction: null as (() => void) | null,
    };
  },
  computed: {
    showStreamManager(): boolean {
      return this.$store.state.showStreamManager;
    },
    activeDeviceSessions(): any[] {
      return this.$store.state.streamSession.activeDeviceSessions;
    },
    activeStreamUid(): number | null {
      return this.$store.state.realtimeStreaming.activeStreamUid;
    },
    activePlaybackRecordingId(): number | null {
      return this.$store.state.realtimeStreaming.activePlaybackRecordingId;
    },
    deviceOptions(): { uid: number; label: string; isConnected: boolean }[] {
      const devices: Device[] = this.$store.state.device.devices || [];
      return devices
        .filter((d) => !this.onlineOnly || d.isConnected)
        .map((d) => ({
          uid: d.uid,
          label: `UID ${d.uid}`,
          isConnected: !!d.isConnected,
        }))
        .sort((a, b) => a.uid - b.uid);
    },
    filteredDeviceOptions(): {
      uid: number;
      label: string;
      isConnected: boolean;
    }[] {
      if (!this.uidFilter) return this.deviceOptions;
      return this.deviceOptions.filter((d) =>
        String(d.uid).includes(this.uidFilter)
      );
    },
    pigRunOptions(): { label: string; value: number }[] {
      return this.pigRuns
        .map((p) => ({ label: p.name, value: p.pigRunId }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    activeFilterCount(): number {
      let n = 0;
      if (this.recordingUidFilter != null && this.recordingUidFilter !== 0) {
        n++;
      }
      if (this.selectedPigRunId != null) n++;
      return n;
    },
    filteredRecordings(): any[] {
      if (this.selectedPigRunId == null) return this.recordings;
      return this.recordings.filter(
        (r: any) => r.pigRunId === this.selectedPigRunId
      );
    },
  },
  watch: {
    showStreamManager(visible: boolean) {
      if (visible) {
        this.autoPopulateUidFilter();
        this.fetchRecordings();
        this.showInstructions = true;
      } else {
        this.showInstructions = false;
      }
    },
  },
  mounted() {
    // If panel is already visible on mount, populate and fetch immediately.
    if (this.showStreamManager) {
      this.autoPopulateUidFilter();
      this.fetchRecordings();
      this.showInstructions = true;
    }

    // Auto-refresh recordings when a recording starts or stops, here or
    // (EN-1261) for anyone in the group while this panel is open.
    this.unsubscribeAction = subscribeRecordingRefresh(
      this.$store,
      (type) =>
        this.fetchRecordings({
          background: isBackgroundRecordingRefresh(type),
        }),
      () => this.showStreamManager
    );
  },
  beforeUnmount() {
    if (this.unsubscribeAction) {
      this.unsubscribeAction();
    }
  },
  methods: {
    autoPopulateUidFilter() {
      if (!this.recordingUidFilter && this.activeStreamUid) {
        this.recordingUidFilter = this.activeStreamUid;
      }
    },
    refresh() {
      this.$store.dispatch('fetchActiveStreamSessions');
    },
    onClose() {
      this.$store.dispatch('hideStreamManager');
    },
    fetchDevices() {
      this.loadingDevices = true;
      this.$store.dispatch('getDevices');
      // getDevices is fire-and-forget; give it a moment then clear the spinner
      setTimeout(() => {
        this.loadingDevices = false;
      }, 1500);
    },
    onFilterDevices(val: string, update: (fn: () => void) => void) {
      update(() => {
        this.uidFilter = val;
      });
    },
    async startSession() {
      if (this.selectedDevice === null) return;
      const uid = this.selectedDevice;
      this.selectedDevice = null;
      this.showInstructions = false;
      const joined = await this.$store.dispatch('joinStreamSession', uid);
      if (joined) {
        this.$store.dispatch('openAdditionalStream', uid);
      }
    },
    async openStream(uid: number) {
      this.showInstructions = false;
      const joined = await this.$store.dispatch('joinStreamSession', uid);
      if (joined) {
        this.$store.dispatch('openAdditionalStream', uid);
      }
    },
    leaveStream(uid: number) {
      this.$store.dispatch('leaveStreamSession', uid);
      this.$store.dispatch('closeStream', uid);
    },
    isStreamOpen(uid: number): boolean {
      return this.$store.state.realtimeStreaming.streamingUid === uid;
    },
    isViewerOn(device: any): boolean {
      const userId = this.$store.state.user?.id;
      if (!userId) return false;
      return device.sessions.some((s: any) => s.userId === userId);
    },
    formatTime(isoString: string): string {
      if (!isoString) return '';
      const d = new Date(isoString);
      return d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    // options.background: a refresh from another user's broadcast. If it
    // fails, keep the list the user is looking at (EN-1261). The button's
    // @click passes a MouseEvent here, which is not a background refresh.
    async fetchRecordings(options?: { background?: boolean }) {
      const background = options?.background === true;
      try {
        const response = this.recordingUidFilter
          ? await StreamService.getRecordingsForUid(this.recordingUidFilter)
          : await StreamService.getRecordings();
        this.recordings = response.data.recordings || [];
      } catch (err) {
        console.error('Failed to fetch recordings:', err);
        this.recordings = recordingsAfterFailedFetch(
          this.recordings,
          background
        );
      }
    },
    async fetchPigRuns() {
      // Cache after first fetch; user can close/reopen the dropdown without
      // re-hitting the API. The list is small enough that this is fine for
      // a filtering control.
      if (this.pigRuns.length > 0) return;
      this.loadingPigRuns = true;
      try {
        const response = await PigRunService.getPigRuns();
        const list = Array.isArray(response.data) ? response.data : [];
        this.pigRuns = list
          .filter((p: any) => p.pigRunId != null && p.name)
          .map((p: any) => ({ pigRunId: p.pigRunId, name: p.name }));
      } catch (err) {
        console.error('Failed to fetch pig runs:', err);
        this.pigRuns = [];
      } finally {
        this.loadingPigRuns = false;
      }
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
      // Lazy-load pig runs the first time the panel is opened. q-select's
      // popup-show event was unreliable here, so we trigger the fetch when
      // the filter panel itself opens.
      if (this.showFilters) {
        this.fetchPigRuns();
      }
    },
    applyFilters() {
      this.fetchRecordings();
    },
    clearFilters() {
      this.recordingUidFilter = null;
      this.selectedPigRunId = null;
      this.fetchRecordings();
    },
    formatRecordingTime(isoString: string): string {
      if (!isoString) return '';
      const d = new Date(isoString);
      return d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    },
    formatRecordingDuration(start: string, end: string): string {
      const ms = new Date(end).getTime() - new Date(start).getTime();
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    },
    playbackRecording(streamRecordingId: number) {
      const rec = this.recordings.find(
        (r: any) => r.streamRecordingId === streamRecordingId
      );
      if (!rec) return;
      this.showInstructions = false;
      this.$store.dispatch('openPlayback', {
        streamRecordingId,
        uid: rec.uid,
        startedDateUtc: rec.startedDateUtc,
      });
    },
    async stopRecording(streamRecordingId: number) {
      try {
        await StreamService.stopRecording(streamRecordingId);
        this.$store.dispatch('recordingStopped', {
          uid: this.recordings.find(
            (r: any) => r.streamRecordingId === streamRecordingId
          )?.uid,
        });
        this.fetchRecordings();
      } catch (err) {
        console.error('Failed to stop recording:', err);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to stop recording',
        });
      }
    },
    toggleNotes(streamRecordingId: number) {
      if (this.expandedRecordingId === streamRecordingId) {
        this.cancelNotesEdit();
        return;
      }
      const rec = this.recordings.find(
        (r: any) => r.streamRecordingId === streamRecordingId
      );
      this.notesDraft = rec?.notes ?? '';
      this.expandedRecordingId = streamRecordingId;
    },
    cancelNotesEdit() {
      this.expandedRecordingId = null;
      this.notesDraft = '';
      this.savingNotes = false;
    },
    async saveNotes(rec: any) {
      this.savingNotes = true;
      try {
        const trimmed = this.notesDraft.trim();
        const payload = trimmed.length > 0 ? trimmed : null;
        await StreamService.updateRecordingNotes(
          rec.streamRecordingId,
          payload
        );
        // Update local copy so the icon/state reflects the save without a
        // full refetch.
        rec.notes = payload;
        this.cancelNotesEdit();
      } catch (err) {
        console.error('Failed to save recording notes:', err);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to save note',
        });
      } finally {
        this.savingNotes = false;
      }
    },
    confirmDeleteRecording(streamRecordingId: number) {
      this.$q
        .dialog({
          title: 'Delete Recording',
          message: 'Are you sure you want to delete this recording?',
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          try {
            await StreamService.deleteRecording(streamRecordingId);
            this.recordings = this.recordings.filter(
              (r: any) => r.streamRecordingId !== streamRecordingId
            );
          } catch (err) {
            console.error('Failed to delete recording:', err);
            this.$q.notify({
              type: 'negative',
              message: 'Failed to delete recording',
            });
          }
        });
    },
  },
});
</script>

<style scoped>
.stream-active-item {
  background-color: rgba(33, 150, 243, 0.08);
  border-left: 3px solid #2196f3;
}
.recording-row {
  border-bottom: 1px solid #e0e0e0;
}
.recording-row:last-child {
  border-bottom: none;
}
.playback-active-item {
  background-color: rgba(76, 175, 80, 0.08);
  border-left: 3px solid #4caf50;
}
</style>
